import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Tabs, Tab, Grid, Card, CardContent,
  CardActions, Button, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
  Alert, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Tooltip, Divider, FormControlLabel, Switch,
  Select, InputLabel, FormControl, OutlinedInput
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ContextNavBar from '../components/ContextNavBar';
import api from '../axios';
import Footer from '../components/Footer';

ChartJS.register(ArcElement, ChartTooltip, Legend);

const DIAS_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const STATUS_LABELS = { disponivel: 'Disponível', confirmado: 'Confirmado', liberado: 'Liberado' };
const STATUS_COLORS = { disponivel: 'info', confirmado: 'success', liberado: 'default' };

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ─── Departamentos ─────────────────────────────────────────────────────────

function TabDepartamentos({ deps, loading, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nome: '', descricao: '', cor: '#6B7280' });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const abrirNovo = () => { setEditando(null); setForm({ nome: '', descricao: '', cor: '#6B7280' }); setErro(''); setOpen(true); };
  const abrirEditar = (d) => { setEditando(d); setForm({ nome: d.nome, descricao: d.descricao || '', cor: d.cor || '#6B7280' }); setErro(''); setOpen(true); };

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Nome é obrigatório.'); return; }
    setSalvando(true); setErro('');
    try {
      if (editando) await api.put(`/voluntariado/departamentos/${editando.id}`, form);
      else await api.post('/voluntariado/departamentos', form);
      setOpen(false); onRefresh();
    } catch (e) { setErro(e?.response?.data?.detail || 'Erro ao salvar.'); }
    finally { setSalvando(false); }
  };

  const excluir = async (d) => {
    if (!window.confirm(`Excluir departamento "${d.nome}"? Todos os eventos vinculados serão removidos.`)) return;
    try { await api.delete(`/voluntariado/departamentos/${d.id}`); onRefresh(); }
    catch (e) { alert(e?.response?.data?.detail || 'Erro ao excluir.'); }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Departamentos</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>Novo departamento</Button>
      </Box>

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {deps.length === 0 && (
            <Grid item xs={12}><Alert severity="info">Nenhum departamento cadastrado.</Alert></Grid>
          )}
          {deps.map((d) => (
            <Grid item xs={12} sm={6} md={4} key={d.id}>
              <Card elevation={2} sx={{ borderRadius: 2, borderTop: `4px solid ${d.cor}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: d.cor, flexShrink: 0 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{d.nome}</Typography>
                  </Box>
                  {d.descricao && <Typography variant="body2" color="text.secondary">{d.descricao}</Typography>}
                  <Chip label={`${d.total_eventos} evento(s)`} size="small" sx={{ mt: 1 }} />
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <IconButton size="small" onClick={() => abrirEditar(d)} aria-label="Editar"><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => excluir(d)} aria-label="Excluir"><DeleteIcon fontSize="small" /></IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar departamento' : 'Novo departamento'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {erro && <Alert severity="error">{erro}</Alert>}
          <TextField label="Nome *" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} fullWidth />
          <TextField label="Descrição" value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} fullWidth multiline rows={2} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">Cor:</Typography>
            <input type="color" value={form.cor} onChange={e => setForm(f => ({ ...f, cor: e.target.value }))}
              style={{ width: 48, height: 36, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
            <Typography variant="caption" color="text.secondary">{form.cor}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>
            {salvando ? <CircularProgress size={18} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Agenda (Eventos) ──────────────────────────────────────────────────────

function TabAgenda({ deps, eventos, loading, onRefresh, dataInicio, dataFim, setDataInicio, setDataFim }) {
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ departamento_id: '', titulo: '', descricao: '', data: '', dia_semana: '', recorrente: false, hora_inicio: '', hora_fim: '', vagas_maximas: '' });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [filtroDepto, setFiltroDepto] = useState('');
  const [verEscala, setVerEscala] = useState(null);

  const eventosFiltrados = filtroDepto ? eventos.filter(e => e.departamento_id === Number(filtroDepto)) : eventos;

  const abrirNovo = () => { setEditando(null); setForm({ departamento_id: '', titulo: '', descricao: '', data: '', dia_semana: '', recorrente: false, hora_inicio: '', hora_fim: '', vagas_maximas: '' }); setErro(''); setOpen(true); };
  const abrirEditar = (ev) => {
    setEditando(ev);
    setForm({
      departamento_id: ev.departamento_id, titulo: ev.titulo, descricao: ev.descricao || '',
      data: ev.data || '', dia_semana: ev.dia_semana || '', recorrente: ev.recorrente || false,
      hora_inicio: ev.hora_inicio || '', hora_fim: ev.hora_fim || '',
      vagas_maximas: ev.vagas_maximas ?? '',
    });
    setErro(''); setOpen(true);
  };

  const salvar = async () => {
    if (!form.departamento_id) { setErro('Selecione o departamento.'); return; }
    if (!form.titulo.trim()) { setErro('Título é obrigatório.'); return; }
    setSalvando(true); setErro('');
    const payload = { ...form, vagas_maximas: form.vagas_maximas !== '' ? Number(form.vagas_maximas) : null };
    try {
      if (editando) await api.put(`/voluntariado/eventos/${editando.id}`, payload);
      else await api.post('/voluntariado/eventos', payload);
      setOpen(false); onRefresh();
    } catch (e) { setErro(e?.response?.data?.detail || 'Erro ao salvar.'); }
    finally { setSalvando(false); }
  };

  const excluir = async (ev) => {
    if (!window.confirm(`Excluir evento "${ev.titulo}"?`)) return;
    try { await api.delete(`/voluntariado/eventos/${ev.id}`); onRefresh(); }
    catch (e) { alert(e?.response?.data?.detail || 'Erro ao excluir.'); }
  };

  return (
    <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Agenda de Eventos</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap' }}>
          <TextField select label="Filtrar por departamento" size="small" value={filtroDepto} onChange={e => setFiltroDepto(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">Todos</MenuItem>
            {deps.map(d => <MenuItem key={d.id} value={d.id}>{d.nome}</MenuItem>)}
          </TextField>
          <TextField label="Data início" type="date" size="small" value={dataInicio} onChange={e => setDataInicio(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
          <TextField label="Data fim" type="date" size="small" value={dataFim} onChange={e => setDataFim(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>Novo evento</Button>
        </Box>
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Título</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Departamento</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Data / Recorrência</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Horário</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Vagas</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Escala</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {eventosFiltrados.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">Nenhum evento cadastrado.</TableCell></TableRow>
              )}
              {eventosFiltrados.map(ev => (
                <TableRow key={ev.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{ev.titulo}</Typography>
                    {ev.descricao && <Typography variant="caption" color="text.secondary">{ev.descricao}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip label={ev.departamento_nome} size="small" sx={{ bgcolor: ev.departamento_cor, color: '#fff', fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    {ev.recorrente
                      ? <Chip label={`Todo(a) ${ev.dia_semana}`} size="small" color="primary" />
                      : <Typography variant="body2">{ev.data ? new Date(ev.data + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</Typography>
                    }
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ev.hora_inicio || '—'}{ev.hora_fim ? ` – ${ev.hora_fim}` : ''}</Typography>
                  </TableCell>
                  <TableCell>
                    {ev.vagas_maximas
                      ? <Chip label={`${ev.confirmados}/${ev.vagas_maximas}`} size="small" color={ev.confirmados >= ev.vagas_maximas ? 'error' : 'success'} />
                      : <Chip label={`${ev.total_escalados} escalado(s)`} size="small" />}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Ver escala">
                      <IconButton size="small" onClick={() => setVerEscala(ev)} aria-label="Ver escala">
                        <AssignmentIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => abrirEditar(ev)} aria-label="Editar"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => excluir(ev)} aria-label="Excluir"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog form evento */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar evento' : 'Novo evento'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {erro && <Alert severity="error">{erro}</Alert>}
          <TextField select label="Departamento *" value={form.departamento_id} onChange={e => setForm(f => ({ ...f, departamento_id: e.target.value }))} fullWidth>
            {deps.map(d => <MenuItem key={d.id} value={d.id}>{d.nome}</MenuItem>)}
          </TextField>
          <TextField label="Título *" value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} fullWidth />
          <TextField label="Descrição" value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} fullWidth multiline rows={2} />
          <FormControlLabel control={<Switch checked={form.recorrente} onChange={e => setForm(f => ({ ...f, recorrente: e.target.checked }))} />} label="Evento recorrente (semanal)" />
          {form.recorrente
            ? <TextField select label="Dia da semana" value={form.dia_semana} onChange={e => setForm(f => ({ ...f, dia_semana: e.target.value }))} fullWidth>
                {DIAS_SEMANA.map(d => <MenuItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</MenuItem>)}
              </TextField>
            : <TextField label="Data" type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
          }
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Início" type="time" value={form.hora_inicio} onChange={e => setForm(f => ({ ...f, hora_inicio: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Fim" type="time" value={form.hora_fim} onChange={e => setForm(f => ({ ...f, hora_fim: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
          </Box>
          <TextField label="Vagas máximas (em branco = ilimitado)" type="number" value={form.vagas_maximas} onChange={e => setForm(f => ({ ...f, vagas_maximas: e.target.value }))} fullWidth inputProps={{ min: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>
            {salvando ? <CircularProgress size={18} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog escala do evento */}
      {verEscala && (
        <DialogEscalaEvento evento={verEscala} onClose={() => { setVerEscala(null); onRefresh(); }} />
      )}
    </>
  );
}

// ─── Dialog Escala de um Evento ────────────────────────────────────────────

function DialogEscalaEvento({ evento, onClose }) {
  const [escala, setEscala] = useState(null);
  const [todos, setTodos] = useState([]);
  const [voluntarioSel, setVoluntarioSel] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [escRes, volRes] = await Promise.all([
        api.get(`/voluntariado/eventos/${evento.id}/escala`),
        api.get('/voluntariado/voluntarios'),
      ]);
      setEscala(escRes.data);
      setTodos(volRes.data);
    } finally { setCarregando(false); }
  }, [evento.id]);

  useEffect(() => { carregar(); }, [carregar]);

  const adicionarVoluntario = async () => {
    if (!voluntarioSel) return;
    setSalvando(true); setErro('');
    try {
      await api.post(`/voluntariado/eventos/${evento.id}/escala`, { voluntario_id: Number(voluntarioSel), status: 'confirmado' });
      setVoluntarioSel('');
      await carregar();
    } catch (e) { setErro(e?.response?.data?.detail || 'Erro.'); }
    finally { setSalvando(false); }
  };

  const alterarStatus = async (escalaId, novoStatus) => {
    try {
      await api.patch(`/voluntariado/escala/${escalaId}`, { status: novoStatus });
      await carregar();
    } catch (e) { alert(e?.response?.data?.detail || 'Erro.'); }
  };

  const remover = async (escalaId) => {
    if (!window.confirm('Remover este voluntário da escala?')) return;
    try {
      await api.delete(`/voluntariado/escala/${escalaId}`);
      await carregar();
    } catch (e) { alert(e?.response?.data?.detail || 'Erro.'); }
  };

  const jaEscalados = new Set((escala?.escala || []).map(s => s.voluntario_id));
  const disponiveis = todos.filter(v => !jaEscalados.has(v.id));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Escala — {evento.titulo}
        <Typography variant="caption" color="text.secondary" display="block">
          {evento.recorrente ? `Todo(a) ${evento.dia_semana}` : evento.data ? new Date(evento.data + 'T00:00:00').toLocaleDateString('pt-BR') : ''}
          {evento.hora_inicio ? ` · ${evento.hora_inicio}` : ''}{evento.hora_fim ? ` – ${evento.hora_fim}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        {carregando ? <CircularProgress /> : (
          <>
            {/* Adicionar voluntário */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'flex-end', flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField select label="Adicionar voluntário" value={voluntarioSel} onChange={e => setVoluntarioSel(e.target.value)} sx={{ flex: 1 }} size="small">
                {disponiveis.length === 0 && <MenuItem value="">Todos os voluntários já estão na escala</MenuItem>}
                {disponiveis.map(v => <MenuItem key={v.id} value={v.id}>{v.nome}{v.telefone ? ` · ${v.telefone}` : ''}</MenuItem>)}
              </TextField>
              <Button variant="contained" startIcon={<PersonAddIcon />} onClick={adicionarVoluntario} disabled={!voluntarioSel || salvando} size="small">
                Adicionar
              </Button>
            </Box>

            {/* Tabela escala atual */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Voluntário</TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Telefone</TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!escala?.escala || escala.escala.length === 0) && (
                    <TableRow><TableCell colSpan={4} align="center">Nenhum voluntário escalado.</TableCell></TableRow>
                  )}
                  {(escala?.escala || []).map(s => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.nome}</TableCell>
                      <TableCell>{s.telefone || '—'}</TableCell>
                      <TableCell>
                        <Chip label={STATUS_LABELS[s.status] || s.status} color={STATUS_COLORS[s.status] || 'default'} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        {s.status !== 'confirmado' && (
                          <Tooltip title="Confirmar">
                            <IconButton size="small" color="success" onClick={() => alterarStatus(s.id, 'confirmado')} aria-label="Confirmar">
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {s.status !== 'liberado' && (
                          <Tooltip title="Liberar">
                            <IconButton size="small" color="warning" onClick={() => alterarStatus(s.id, 'liberado')} aria-label="Liberar">
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remover da escala">
                          <IconButton size="small" color="error" onClick={() => remover(s.id)} aria-label="Remover">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Voluntários ───────────────────────────────────────────────────────────

function TabVoluntarios({ deps, voluntarios, membros, loading, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nome: '', telefone: '', email: '', habilidades: '', observacoes: '', departamentos: [], membro_id: null, ativo: true });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [filtroDep, setFiltroDep] = useState('');

  const abrirNovo = () => {
    setEditando(null);
    setForm({ nome: '', telefone: '', email: '', habilidades: '', observacoes: '', departamentos: [], membro_id: null, ativo: true });
    setErro(''); setOpen(true);
  };
  const abrirEditar = (v) => {
    setEditando(v);
    setForm({ nome: v.nome, telefone: v.telefone || '', email: v.email || '', habilidades: v.habilidades || '', observacoes: v.observacoes || '', departamentos: v.departamentos.map(d => d.id), membro_id: v.membro_id || null, ativo: v.ativo !== false });
    setErro(''); setOpen(true);
  };

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Nome é obrigatório.'); return; }
    if (form.ativo && !form.membro_id) { setErro('Um voluntário ativo deve estar vinculado a um membro oficialmente registrado da instituição.'); return; }
    setSalvando(true); setErro('');
    try {
      if (editando) await api.put(`/voluntariado/voluntarios/${editando.id}`, form);
      else await api.post('/voluntariado/voluntarios', form);
      setOpen(false); onRefresh();
    } catch (e) { setErro(e?.response?.data?.detail || 'Erro ao salvar.'); }
    finally { setSalvando(false); }
  };

  const excluir = async (v) => {
    if (!window.confirm(`Excluir voluntário "${v.nome}"?`)) return;
    try { await api.delete(`/voluntariado/voluntarios/${v.id}`); onRefresh(); }
    catch (e) { alert(e?.response?.data?.detail || 'Erro ao excluir.'); }
  };

  const filtrados = filtroDep ? voluntarios.filter(v => v.departamentos.some(d => d.id === Number(filtroDep))) : voluntarios;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Voluntários</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap' }}>
          <TextField select label="Filtrar por departamento" size="small" value={filtroDep} onChange={e => setFiltroDep(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">Todos</MenuItem>
            {deps.map(d => <MenuItem key={d.id} value={d.id}>{d.nome}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>Novo voluntário</Button>
        </Box>
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Departamentos</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Habilidades</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Serviços</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrados.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">Nenhum voluntário cadastrado.</TableCell></TableRow>
              )}
              {filtrados.map(v => (
                <TableRow key={v.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{v.nome}</TableCell>
                  <TableCell>{v.telefone || '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {v.departamentos.length === 0 ? <Typography variant="caption" color="text.secondary">—</Typography>
                        : v.departamentos.map(d => <Chip key={d.id} label={d.nome} size="small" sx={{ bgcolor: d.cor, color: '#fff', fontWeight: 600 }} />)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v.habilidades || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={`${v.total_eventos_confirmados} confirmado(s)`} size="small" color="success" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => abrirEditar(v)} aria-label="Editar"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => excluir(v)} aria-label="Excluir"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar voluntário' : 'Novo voluntário'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {erro && <Alert severity="error">{erro}</Alert>}
          <TextField label="Nome *" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} fullWidth />
          <TextField label="Telefone / WhatsApp" value={form.telefone} onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))} fullWidth />
          <TextField label="E-mail" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth />
          <FormControlLabel control={<Switch checked={form.ativo} onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))} />} label="Ativo" />
          {form.ativo && (
            <TextField select label="Membro *" value={form.membro_id || ''} onChange={e => setForm(f => ({ ...f, membro_id: e.target.value ? Number(e.target.value) : null }))} fullWidth error={form.ativo && !form.membro_id} helperText={form.ativo && !form.membro_id ? 'Selecione um membro para ativar o voluntário' : ''}>
              <MenuItem value="">Selecione um membro</MenuItem>
              {(membros || []).map(m => <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>)}
            </TextField>
          )}
          <TextField label="Habilidades" value={form.habilidades} onChange={e => setForm(f => ({ ...f, habilidades: e.target.value }))} fullWidth placeholder="ex.: musica, tecnologia, crianças..." />
          <FormControl fullWidth>
            <InputLabel>Departamentos</InputLabel>
            <Select multiple value={form.departamentos} onChange={e => setForm(f => ({ ...f, departamentos: e.target.value }))} input={<OutlinedInput label="Departamentos" />}
              renderValue={(sel) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {sel.map(id => { const d = deps.find(d => d.id === id); return d ? <Chip key={id} label={d.nome} size="small" sx={{ bgcolor: d.cor, color: '#fff' }} /> : null; })}
                </Box>
              )}>
              {deps.map(d => <MenuItem key={d.id} value={d.id}>{d.nome}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Observações" value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} fullWidth multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>
            {salvando ? <CircularProgress size={18} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────────────

function TabDashboard({ dashData, loading }) {
  if (loading) return <CircularProgress />;
  if (!dashData) return <Alert severity="info">Nenhum dado disponível.</Alert>;

  const pieData = {
    labels: dashData.eventos_por_departamento.map(e => e.departamento),
    datasets: [{
      data: dashData.eventos_por_departamento.map(e => e.total),
      backgroundColor: ['#00C48C', '#0369A1', '#065F46', '#B91C1C', '#B45309', '#0E7490', '#6D28D9', '#15803D'],
    }],
  };

  const cards = [
    { label: 'Departamentos', value: dashData.departamentos, icon: <BusinessIcon />, cor: '#00C48C' },
    { label: 'Eventos ativos', value: dashData.eventos, icon: <EventIcon />, cor: '#0369A1' },
    { label: 'Voluntários', value: dashData.voluntarios, icon: <PeopleIcon />, cor: '#065F46' },
    { label: 'Escalas pendentes', value: dashData.escalas_pendentes, icon: <AssignmentIcon />, cor: '#B45309' },
    { label: 'Escalas confirmadas', value: dashData.escalas_confirmadas, icon: <CheckCircleIcon />, cor: '#15803D' },
  ];

  return (
    <>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map(s => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={s.label}>
            <Card elevation={2} sx={{ borderRadius: 2, textAlign: 'center' }}>
              <CardContent>
                <Box sx={{ bgcolor: s.cor, color: '#fff', p: 1.5, borderRadius: 2, display: 'inline-flex', mb: 1 }}>{s.icon}</Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>{s.value}</Typography>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {dashData.eventos_por_departamento.length > 0 && (
        <Card elevation={2} sx={{ borderRadius: 2, maxWidth: 400, mx: 'auto' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>Eventos por Departamento</Typography>
            <Box sx={{ maxWidth: 300, mx: 'auto' }}>
              <Pie data={pieData} />
            </Box>
          </CardContent>
        </Card>
      )}
    </>
  );
}

// ─── Página principal ──────────────────────────────────────────────────────

export default function AreaVoluntariado({ onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [deps, setDeps] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [voluntarios, setVoluntarios] = useState([]);
  const [dashData, setDashData] = useState(null);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregarDashboard = async () => {
    try {
      const res = await api.get('/voluntariado/dashboard');
      setDashData(res.data);
    } catch {
      // silent
    }
  };

  const carregar = useCallback(async () => {
    setLoading(true); setErro('');
    try {
      const params = {};
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;
      const [dRes, eRes, vRes] = await Promise.all([
        api.get('/voluntariado/departamentos'),
        api.get('/voluntariado/eventos', { params }),
        api.get('/voluntariado/voluntarios'),
      ]);
      setDeps(dRes.data);
      setEventos(eRes.data);
      setVoluntarios(vRes.data);
    } catch {
      setErro('Não foi possível carregar os dados.');
    } finally { setLoading(false); }
  }, [dataInicio, dataFim]);

  useEffect(() => {
    carregar();
    carregarDashboard();
  }, [carregar]);

  return (
    <>
      <Header onLogout={onLogout} />
      <ContextNavBar context="voluntariado" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>

        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/home')} size="small">Voltar</Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ bgcolor: '#00C48C', color: '#fff', p: 1.5, borderRadius: 2, display: 'flex' }}>
            <PeopleIcon fontSize="large" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Voluntariado</Typography>
            <Typography variant="body1" color="text.secondary">Gerencie departamentos, agenda e escala de voluntários.</Typography>
          </Box>
        </Box>

        {erro && <Alert severity="warning" sx={{ mb: 2 }}>{erro}</Alert>}

        {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="Seções do voluntariado" variant={window.innerWidth < 600 ? 'scrollable' : 'standard'} scrollButtons="auto">
            <Tab icon={<DashboardIcon />} iconPosition="start" label="Dashboard" />
            <Tab icon={<BusinessIcon />} iconPosition="start" label="Departamentos" />
            <Tab icon={<EventIcon />} iconPosition="start" label="Agenda" />
            <Tab icon={<PeopleIcon />} iconPosition="start" label="Voluntários" />
          </Tabs>
        </Box>

        <TabPanel value={tab} index={0}>
          <TabDashboard dashData={dashData} loading={loading} />
        </TabPanel>
        <TabPanel value={tab} index={1}>
          <TabDepartamentos deps={deps} loading={loading} onRefresh={carregar} />
        </TabPanel>
        <TabPanel value={tab} index={2}>
          <TabAgenda
            deps={deps}
            eventos={eventos}
            loading={loading}
            onRefresh={carregar}
            dataInicio={dataInicio}
            dataFim={dataFim}
            setDataInicio={setDataInicio}
            setDataFim={setDataFim}
          />
        </TabPanel>
        <TabPanel value={tab} index={3}>
          <TabVoluntarios deps={deps} voluntarios={voluntarios} loading={loading} onRefresh={carregar} />
        </TabPanel>

      </Container>
    <Footer />
    </>
  );
}
